import Canvas from "../classes/Canvas";
import {
  ImageFilterProps,
  ImageFilterType,
  ObjectFitType,
} from "../interfaces";
import {
  ActiveSelection,
  FabricImage,
  filters,
  FabricImageProps,
  FabricObject,
  FabricPath,
  util,
  FabricObjectProps,
  FabricPattern,
} from "../objects";

export const createImageObject = (options: Partial<FabricImageProps> = {}) => {
  const imageElement = new Image();
  imageElement.crossOrigin = "anonymous";
  const imageObject = new FabricImage(imageElement, {
    ...options,
    objectFit: ObjectFitType.Contain,
  });
  return imageObject;
};

export const createPathObject = (
  pathString: string,
  options: Partial<FabricObjectProps> = {},
) => {
  return new FabricPath(pathString, {
    fill: "transparent",
    stroke: "red",
    strokeWidth: 4,
    ...options,
  });
};

export const createFilterObject = (
  filter: ImageFilterProps = { type: ImageFilterType.None },
) => {
  const { type, color = "#ffffff" } = filter;
  switch (type) {
    case "Grayscale": {
      return [new filters.Grayscale()];
    }
    case "Blend color":
      return [new filters.BlendColor({ color })];
    default:
      return [];
  }
};

const degreeToRad = (deg: number) => (deg * Math.PI) / 180;

export const ellipseArcPath = (
  rx: number,
  ry: number,
  startAngle: number,
  endAngle: number,
  flip = false,
) => {
  if (flip) {
    startAngle = (startAngle + 180) % 360;
    endAngle = (endAngle + 180) % 360;
  }

  const x1 = -rx * Math.cos(degreeToRad(startAngle));
  const y1 = ry * Math.sin(degreeToRad(startAngle));
  const x2 = -rx * Math.cos(degreeToRad(endAngle));
  const y2 = ry * Math.sin(degreeToRad(endAngle));

  const angleDiff = (endAngle - startAngle + 360) % 360;
  const largeArcFlag = angleDiff > 180 ? 1 : 0;
  const sweepFlag = 1;

  return `M ${x1} ${y1} A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${x2} ${y2}`;
};

const twoPi = 2 * Math.PI;

const normalize = (angle: number) => ((angle % twoPi) + twoPi) % twoPi;

const isAngleInArc = (theta: number, a: number, b: number) => {
  theta = normalize(theta);
  a = normalize(a);
  b = normalize(b);
  if (a < b) return a <= theta && theta <= b;
  return a <= theta || theta <= b;
};

export const getEllipseArcCenterPoint = (
  rx: number,
  ry: number,
  a: number,
  b: number,
) => {
  a = degreeToRad(a);
  b = degreeToRad(b);
  const criticalAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
  const points = [
    [rx * Math.cos(a), -ry * Math.sin(a)],
    [rx * Math.cos(b), -ry * Math.sin(b)],
  ];
  for (const theta of criticalAngles) {
    if (isAngleInArc(theta, a, b)) {
      points.push([rx * Math.cos(theta), -ry * Math.sin(theta)]);
    }
  }
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
  };
};

type ObjectFitOrigin = {
  originX: "left" | "center" | "right";
  originY: "top" | "center" | "bottom";
};

const isObjectFitOriginX = (
  position: string,
): position is ObjectFitOrigin["originX"] =>
  position === "left" || position === "right";

const isObjectFitOriginY = (
  position: string,
): position is ObjectFitOrigin["originY"] =>
  position === "top" || position === "bottom";

const isCoverObjectFit = (objectFit: ObjectFitType) =>
  objectFit.split("-")[0] === ObjectFitType.Cover;

const getObjectFitOrigin = (objectFit: ObjectFitType): ObjectFitOrigin => {
  const positions = objectFit.split("-").slice(1);
  return {
    originX: positions.find(isObjectFitOriginX) || "center",
    originY: positions.find(isObjectFitOriginY) || "center",
  };
};

export const fitObject = (
  object: FabricObject,
  container: FabricObject,
  objectFit: ObjectFitType = ObjectFitType.Fill,
) => {
  const { width: containerWidth, height: containerHeight } = container;
  switch (objectFit) {
    case ObjectFitType.Fill: {
      object
        .set({
          width: containerWidth,
          height: containerHeight,
          left: -containerWidth / 2,
          top: -containerHeight / 2,
        })
        .scale(1);
      return;
    }
    default: {
      const { width: layerWidth, height: layerHeight } = object;
      if (layerWidth * layerHeight === 0) return;
      const scaleX = containerWidth / layerWidth;
      const scaleY = containerHeight / layerHeight;
      const scale = isCoverObjectFit(objectFit)
        ? Math.max(scaleX, scaleY)
        : Math.min(scaleX, scaleY);
      const scaledWidth = layerWidth * scale;
      const scaledHeight = layerHeight * scale;
      const { originX, originY } = getObjectFitOrigin(objectFit);
      let left = -scaledWidth / 2;
      let top = -scaledHeight / 2;

      if (originX === "left") {
        left = -containerWidth / 2;
      } else if (originX === "right") {
        left = containerWidth / 2 - scaledWidth;
      }

      if (originY === "top") {
        top = -containerHeight / 2;
      } else if (originY === "bottom") {
        top = containerHeight / 2 - scaledHeight;
      }

      object.set({ left, top }).scale(scale);
      return;
    }
  }
};

export const fitObjectPattern = (
  pattern: FabricPattern,
  container: FabricObject,
  objectFit: ObjectFitType = ObjectFitType.Fill,
  patternScale: number = 1,
) => {
  const patternSrc = pattern.source as HTMLImageElement;
  if (!patternSrc) return;
  const { width: imgWidth, height: imgHeight } = patternSrc;
  const { width: containerWidth, height: containerHeight } = container;

  const scaleX = containerWidth / imgWidth;
  const scaleY = containerHeight / imgHeight;

  switch (objectFit) {
    case ObjectFitType.Fill: {
      pattern.patternTransform = [
        scaleX * patternScale,
        0,
        0,
        scaleY * patternScale,
        0,
        0,
      ];
      return;
    }
    default: {
      const scale = isCoverObjectFit(objectFit)
        ? Math.max(scaleX, scaleY)
        : Math.min(scaleX, scaleY);
      pattern.patternTransform = [
        scale * patternScale,
        0,
        0,
        scale * patternScale,
        0,
        0,
      ];
      return;
    }
  }
};

export const fitAbsoluteObject = (
  object: FabricObject,
  container: FabricObject,
  objectFit: ObjectFitType = ObjectFitType.Fill,
) => {
  const { width: objectWidth, height: objectHeight } = object;
  const {
    left: containerLeft,
    top: containerTop,
    width: containerWidth,
    height: containerHeight,
    angle: containerAngle,
    flipX,
    flipY,
  } = container;

  const scaleX = containerWidth / objectWidth;
  const scaleY = containerHeight / objectHeight;

  switch (objectFit) {
    case ObjectFitType.Fill: {
      object.set({
        left: containerLeft,
        top: containerTop,
        angle: containerAngle,
        scaleX,
        scaleY,
        flipX,
        flipY,
      });
      return;
    }

    default: {
      const scale = isCoverObjectFit(objectFit)
        ? Math.max(scaleX, scaleY)
        : Math.min(scaleX, scaleY);
      const { originX, originY } = getObjectFitOrigin(objectFit);

      object.scale(scale);
      object.set({ angle: containerAngle, flipX, flipY });

      const point = container.getPointByOrigin(originX, originY);
      object.setPositionByOrigin(point, originX, originY);
      object.setCoords();
    }
  }
};

export const getObjectAbsoluteProps = (object: FabricObject) => {
  const matrix = object.calcTransformMatrix();

  const { translateX, translateY, scaleX, scaleY, angle } =
    util.qrDecompose(matrix);

  return {
    left: translateX,
    top: translateY,
    width: object.width,
    height: object.height,
    scaleX,
    scaleY,
    angle,
  };
};

export const updateObjectPosition = (object: FabricObject) => {
  const canvas = object.canvas;
  if (!canvas) return;
  const { originX, originY } = canvas.layerOrigin;
  const { x, y } = object.getPointByOrigin(originX, originY);
  object.set({ x, y });
};

export const setObjectPosition = (
  object: FabricObject,
  key: "x" | "y" | { x: number; y: number },
  value: number = 0,
) => {
  const canvas = object.canvas;
  if (!canvas) return;
  const { originX, originY } = canvas.layerOrigin;
  const positionPoint = object.getPointByOrigin(originX, originY);
  if (typeof key === "string") {
    positionPoint[key] = value;
  } else {
    positionPoint.setXY(key.x, key.y);
  }
  object.setPositionByOrigin(positionPoint, originX, originY);
  object.setCoords();
  canvas.requestRenderAll();
};

export const lockObject = (object: FabricObject, locked: boolean = false) => {
  const isActiveSelection = object instanceof ActiveSelection;

  object.set({
    locked,
    lockMovementX: locked,
    lockMovementY: locked,
    lockRotation: locked,
    lockScalingFlip: locked,
    lockScalingX: locked,
    lockScalingY: locked,
    lockSkewingX: locked,
    lockSkewingY: locked,
    selectable: !locked,
    evented: !locked,
  });

  object.setControlsVisibility({
    tl: !locked,
    tr: !locked,
    bl: !locked,
    br: !locked,
    ml: isActiveSelection ? false : !locked,
    mr: isActiveSelection ? false : !locked,
    mt: isActiveSelection ? false : !locked,
    mb: isActiveSelection ? false : !locked,
    mtr: !locked,
  });
};

export const getClipPathTransform = (canvas: Canvas) => {
  const clip = canvas.clipPath;

  const bounds = clip.getBoundingRect();
  const viewportTransform = canvas.viewportTransform;
  const zoom = canvas.getZoom();

  const screenLeft = bounds.left * zoom + viewportTransform[4];
  const screenTop = bounds.top * zoom + viewportTransform[5];
  const screenWidth = clip.width * zoom;
  const screenHeight = clip.height * zoom;

  return {
    left: screenLeft,
    top: screenTop,
    width: screenWidth,
    height: screenHeight,
  };
};

export const sortObjectsByZIndex = (objects: FabricObject[]) => {
  const canvas = objects[0]?.canvas;
  if (!canvas) return objects;

  const canvasObjects = canvas.getObjects();
  return [...objects].sort(
    (l1, l2) => canvasObjects.indexOf(l1) - canvasObjects.indexOf(l2),
  );
};

export const updateActiveSelection = (activeSelection: ActiveSelection) => {
  const objects = activeSelection.getObjects();

  const locked = objects.some((o: any) => o.locked);
  activeSelection.set({
    locked,
    lockMovementX: locked,
    lockMovementY: locked,
    lockRotation: locked,
    lockScalingFlip: locked,
    lockScalingX: locked,
    lockScalingY: locked,
    lockSkewingX: locked,
    lockSkewingY: locked,
    selectable: !locked,
  });
  activeSelection.setControlsVisibility({
    tl: !locked,
    tr: !locked,
    bl: !locked,
    br: !locked,
    ml: !locked,
    mr: !locked,
    mt: !locked,
    mb: !locked,
    mtr: !locked,
  });
};

export const focusObject = (object: FabricObject, focus: boolean) => {
  object.opacity = focus ? 1 : 0;
  object.selectable = (object as any).locked ? false : focus;
  object.evented = object.selectable;
};
